import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next/';
import NumberFormat from 'react-number-format';
import classNames from 'classnames';
import { colors } from 'theme';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import Loading from 'components/Loading';
import { AppContext } from 'context';
import {
  useBuyersFromMaterialWithClientContract,
  useClientContracts,
  useClientsFromMaterialAndBuyerWithClientContract,
  useMaterialsWithClientContract,
  useProvisionalOutboundStocksIndexedByClientId,
  useSortingCenterContractStatusesIndexedByClientIds,
} from 'hooks';
import {
  Buyer,
  Client,
  ExpeditionClientPortionForm,
  ExpeditionForm,
  Material,
  ProvisionalOutboundStock,
  ProvisionalOutboundStocksIndexedByStringId,
  SortingCenterContractStatus,
} from 'types';
import { fixRoundedTotal } from 'utils/array';
import { floatWeightToInt, formatTons } from 'utils/weight';
import { transformId, transformObjectId } from 'services/apiPlatformTransformer';
import { calculatePercentage, calculateWeightFromPercentage, roundToPrecision } from 'services/math';
import PercentageInputField from 'components/CustomInputs/PercentageInputField';
import {
  computeRepartitionByContracts,
  computeRepartitionPercentageFromWeight,
  computeRepartitionWeightFromPercentage,
  transformRepartitionByIndexInFormObjectToArray,
} from 'services/expedition';
import TableSeparatorRow from 'components/TableSeparatorRow';
import { notify } from 'components/Notifier';
import { ExpeditionFormLine } from 'types/expedition';
import { expeditionFormSchema } from './Step2.yup';
import { FormStepComponentProps } from '../MultiStepForm';
import { EVENT_NAMES, track } from '../../../services/pianoAnalytics';
import { ROLE_HIERARCHY } from '../../Header';

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
  },
  previousStepInfo: {
    width: '100%',
    margin: '0 0 12px 0',
    backgroundColor: '#E9EDEF',
    display: 'flex',
    listStyleType: 'none',
    justifyContent: 'center',
    padding: '1.3em 0',
    '& li': {
      margin: '0 4em',
    },
  },
  clientsTable: {
    width: 820,
    marginTop: '30px',
  },
  clientNameHeaderCell: {
    width: '188px',
    fontSize: '16px',
  },
  tonnageEnStockHeaderCell: {
    textAlign: 'right',
    width: '217px',
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    fontSize: '16px',
    padding: '8px 40px',
  },
  partitioningHeaderCell: {
    textAlign: 'right',
    width: '173px',
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    fontSize: '16px',
  },
  percentageHeaderCell: {
    textAlign: 'right',
    width: '182px',
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    fontSize: '16px',
  },
  checkboxHeaderCell: {
    width: '60px',
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    fontSize: '16px',
    textAlign: 'center',
  },
  clientNameCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    padding: '0px 22px',
    width: '188px',
  },
  tonnageEnStockCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    padding: '0px 22px',
    textAlign: 'center',
    width: '217px',
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
  },
  partitioningCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    padding: '0px 22px',
    width: '173px',
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    textAlign: 'right',
  },
  percentageCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    padding: '0px 22px',
    textAlign: 'right',
    width: '182px',
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
  },
  checkboxCell: {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    padding: '0px 22px',
    width: '60px',
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
  },
  totalCell: {
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    padding: '8px 22px',
    textAlign: 'right',
  },
  negativeWeight: {
    color: colors.red,
  },
  bold: {
    fontWeight: 600,
    margin: 0,

    '&:after': {
      content: '" :"',
    },
  },
  tonnageSwitch: {
    display: 'flex',
    flex: 1,
    width: '55%',
    justifyContent: 'flex-end',
  },
  tableDivider: {
    width: '100%',
    height: 1,
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  tableDividerLeft: {
    width: '20%',
    marginLeft: 0,
    marginRight: 12,
  },
  tableDividerRight: {
    width: '60%',
    flexGrow: 1,
    marginLeft: 12,
    marginRight: 0,
  },
  dividerClosure: {
    backgroundColor: theme.palette.primary.main,
  },
  sectionHeader: {
    textTransform: 'none',
    color: theme.palette.primary.main,
  },
  noContracts: {
    color: 'red',
    fontSize: '1.5rem',
    height: '100%',
    justifyContent: 'center',
  },
  noContractsContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    maxWidth: '40%',
    margin: '0 auto',
  },
  warningFields: {
    '& .MuiInputBase-input': {
      color: 'orange',
    },
  },
  errorField: {
    '& .MuiInputBase-input': {
      color: 'red',
    },
  },
}));

type ClientWeightPath = `expeditionClientPortions.${number}.weight`;

type ClientPercentagePath = `expeditionClientPortions.${number}.percentage`;

type CheckboxPath = `expeditionClientPortions.${number}.checked`;

type Step2Props = FormStepComponentProps<ExpeditionForm> & {
  isUpdateMode?: boolean;
  originalExpeditionClients?: Client[];
  buyerForUpdate?: Buyer;
  materialForUpdate?: Material;
  isSubmitting?: boolean;
};

const Step2: React.FC<Step2Props> = ({
  onSubmit,
  formName,
  previousStepData,
  isUpdateMode = false,
  originalExpeditionClients,
  buyerForUpdate,
  materialForUpdate,
  isSubmitting,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'expeditionCreateForm' });
  const { t: tGlobal } = useTranslation();

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  let currentStatus: SortingCenterContractStatus | null = null;
  const roles = context.getRoles?.();
  const user = context.user;

  const [clientContracts] = useClientContracts({
    material: previousStepData.material ? previousStepData.material : '',
    buyer: previousStepData.buyer ? previousStepData.buyer : '',
    isActive: '1',
  });
  const sortingCenterContractStatusesIndexedByClientId = useSortingCenterContractStatusesIndexedByClientIds(
    transformId(previousStepData.material),
    transformId(previousStepData.buyer),
    previousStepData.expeditionDate?.toISOString() ?? null,
  );
  const materialChoices = useMaterialsWithClientContract(clientContracts);
  const selectedMaterialCreate = materialChoices?.find((el: Material) => previousStepData.material === el['@id']);
  const buyerChoices = useBuyersFromMaterialWithClientContract(previousStepData.material, clientContracts);
  const selectedBuyerCreate = buyerChoices?.find((el: Buyer) => previousStepData.buyer === el['@id']);
  const clientsCreate = useClientsFromMaterialAndBuyerWithClientContract(
    previousStepData.material,
    previousStepData.buyer,
    clientContracts,
  );

  const selectedMaterial = isUpdateMode && materialForUpdate ? materialForUpdate : selectedMaterialCreate;
  let sortingCenterId = sortingCenter?.['@id']?.toString();
  let selectedMaterialId = selectedMaterial?.['@id']?.toString();
  sortingCenterId = sortingCenterId?.split('/')[3];
  selectedMaterialId = selectedMaterialId?.split('/')[3];

  const previousPortions = previousStepData.expeditionClientPortions;
  const expeditionHavePercentageSavedInDatabase =
    typeof previousPortions !== 'string' &&
    previousPortions?.[0]?.percentage &&
    null !== previousPortions?.[0]?.percentage;

  const reloadStocks =
    previousPortions &&
    typeof previousPortions !== 'string' &&
    previousPortions.length > 0 &&
    selectedMaterialId &&
    sortingCenterId;
  const overridenStocks = useMemo(
    () =>
      reloadStocks
        ? (previousPortions as ExpeditionClientPortionForm[]).reduce<ProvisionalOutboundStocksIndexedByStringId>(
            (accu, portion) => {
              accu[transformId(portion.client)] = {
                sortingCenter: sortingCenterId as string,
                client: transformId(portion.client),
                material: selectedMaterialId as string,
                weight: portion.availableStockWeight ? portion.availableStockWeight * 1000 : 0,
              };
              return accu;
            },
            {},
          )
        : undefined,
    [previousPortions, reloadStocks, selectedMaterialId, sortingCenterId],
  );
  const clients = isUpdateMode && originalExpeditionClients ? originalExpeditionClients : clientsCreate;
  const selectedBuyer = isUpdateMode && buyerForUpdate ? buyerForUpdate : selectedBuyerCreate;

  const provisionalOutboundStocksIndexedByClientId = useProvisionalOutboundStocksIndexedByClientId(
    clients,
    previousStepData.expeditionDate,
    selectedMaterialId,
    overridenStocks,
  );

  const sortedClients = useMemo(() => {
    if (provisionalOutboundStocksIndexedByClientId && sortingCenterContractStatusesIndexedByClientId) {
      clients
        .filter((client: Client) => provisionalOutboundStocksIndexedByClientId[transformObjectId(client)] !== undefined)
        .sort((clientA, clientB) => clientA.shortName.localeCompare(clientB.shortName))
        .forEach(
          (client: Client) =>
            (client.sortingCenterContractsStatus =
              sortingCenterContractStatusesIndexedByClientId[parseInt(transformObjectId(client))] ??
              SortingCenterContractStatus.UPCOMING),
        );

      return [
        ...clients
          .filter((client: Client) => SortingCenterContractStatus.CLOSED === client.sortingCenterContractsStatus)
          .sort((clientA, clientB) => clientA.shortName.localeCompare(clientB.shortName)),
        ...clients
          .filter(
            (client: Client) => SortingCenterContractStatus.END_OF_PROCESSING === client.sortingCenterContractsStatus,
          )
          .sort((clientA, clientB) => clientA.shortName.localeCompare(clientB.shortName)),
        ...clients
          .filter((client: Client) => SortingCenterContractStatus.ONGOING === client.sortingCenterContractsStatus)
          .sort((clientA, clientB) => clientA.shortName.localeCompare(clientB.shortName)),
      ];
    }

    return clients;
  }, [clients, provisionalOutboundStocksIndexedByClientId, sortingCenterContractStatusesIndexedByClientId]);

  const schema = useMemo(() => expeditionFormSchema(t), [t]);

  const weightsToWatch = useMemo(
    () => sortedClients?.map((_, index): ClientWeightPath => `expeditionClientPortions.${index}.weight`) ?? [],
    [sortedClients],
  );

  const percentagesToWatch = useMemo(
    () => sortedClients?.map((_, index): ClientPercentagePath => `expeditionClientPortions.${index}.percentage`) ?? [],
    [sortedClients],
  );

  const checkboxesToWatch = useMemo(
    () => sortedClients?.map((_, index): CheckboxPath => `expeditionClientPortions.${index}.checked`) ?? [],
    [sortedClients],
  );

  const totalWeightFromStep1 = previousStepData.weight !== '' ? previousStepData.weight : undefined;

  const {
    control,
    handleSubmit,
    register,
    trigger,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors, isDirty },
  } = useForm<ExpeditionForm>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: previousStepData,
    resolver: yupResolver(schema),
  });

  const watchWeights = watch(weightsToWatch);
  // ET-2521 : tonnage réparti en priorité sur les contrats clos
  const [repartitionTonnageWithContractStatusPriority, setRepartitionTonnageWithContractStatusPriority] = useState<
    string[] | null
  >(null);
  const [isComputeRepartitionLoading, setIsComputeRepartitionLoading] = useState(false);
  const watchPercentages = watch(percentagesToWatch);
  const distributionCheckboxes = watch(checkboxesToWatch);
  const [, setIsEditingTonnageChanged] = useState(false);
  const [isEditingTonnage, setIsEditingTonnage] = useState(false);
  const [isEditingPercentage, setIsEditingPercentage] = useState(false);
  const [isSwitchTonnage, setIsSwitchTonnage] = useState(false);

  // when we uncheck a checkbox, we set the handlingCheckbox to the index of the checkbox
  // in order to detect it in a onChange of a corresponding percentage field and ignore
  // the change when it is triggred by an unchecked checkbox
  const [, setHandlingCheckbox] = useState(-1);
  const isFirstRender = useRef(true);
  const isFromActionCheckedCheckbox = useRef(false);
  const [totalWeight, setTotalWeight] = useState(0);
  const [totalPercentage, setTotalPercentage] = useState(100);
  const totalWatchWeight: number =
    watchWeights?.reduce((total, newWeight): number => total + parseFloat(newWeight || '0'), 0) ?? 0;
  const totalWatchPercentage: number =
    watchPercentages?.reduce((total, newPercentage): number => total + parseFloat(newPercentage || '0'), 0) ?? 0;

  const getProvisionalOutboundStock = (client: Client) => {
    return provisionalOutboundStocksIndexedByClientId &&
      provisionalOutboundStocksIndexedByClientId[transformObjectId(client)]
      ? provisionalOutboundStocksIndexedByClientId[transformObjectId(client)].weight / 1000
      : 0;
  };

  const [warningFields, setWarningField] = useState<(number | null)[]>([]);
  const [errorFields, setErrorFields] = useState<(number | null)[]>([]);

  const calculateDistributionWeight = useCallback(
    (isChecked: boolean, provisionalOutboundStock: number, totalProvisionalOutboundStocks: number) => {
      return isChecked && previousStepData?.weight
        ? provisionalOutboundStock > 0
          ? ((previousStepData.weight * provisionalOutboundStock) / totalProvisionalOutboundStocks).toFixed(3)
          : ''
        : '';
    },
    [previousStepData.weight],
  );

  const getTonnageExpedieInKilos = (): number | null => {
    const tonnageInTons = getTonnageExpedieInTons();
    if (tonnageInTons === null) {
      return null;
    }

    const tonnageInKilos = tonnageInTons * 1000;

    // Convert the result to a string and then back to a number to ensure accuracy
    return parseFloat(tonnageInKilos.toFixed(3));
  };

  const getTonnageExpedieInTons = (): number | null => {
    if ('undefined' === typeof previousStepData.weight) return null;
    return 'string' === typeof previousStepData.weight ? parseInt(previousStepData.weight) : previousStepData.weight;
  };
  const resetInitialValues = useCallback(
    (provisionalOutboundStocksIndexedByClientId: ProvisionalOutboundStocksIndexedByStringId) => {
      const totalProvisionalOutboundStocks = Object.values(provisionalOutboundStocksIndexedByClientId).reduce(
        (total: number, provisionalOutboundStock: ProvisionalOutboundStock) =>
          total + Math.max(provisionalOutboundStock.weight, 0),
        0,
      );
      const provisionalOutboundStock = (client: Client) =>
        provisionalOutboundStocksIndexedByClientId[transformObjectId(client)].weight;
      const defaultValues: any = {
        ...previousStepData,
        expeditionClientPortions: sortedClients?.map(client => {
          const portionWeight = calculateDistributionWeight(
            !!(
              provisionalOutboundStocksIndexedByClientId[transformObjectId(client)] &&
              provisionalOutboundStock(client) > 0 &&
              previousStepData?.weight
            ),
            provisionalOutboundStock(client),
            totalProvisionalOutboundStocks,
          );
          const portionPercentage =
            '' === portionWeight
              ? ''
              : calculatePercentage(parseFloat(portionWeight), totalWeightFromStep1 ?? 0, 4).toString();
          return {
            checked: provisionalOutboundStock(client) > 0,
            weight: portionWeight,
            percentage: portionPercentage,
            client: client['@id'],
          };
        }),
      };
      // we round both as rounding one does not imply rounding the other
      fixRoundedTotal(defaultValues.expeditionClientPortions, totalWeightFromStep1, 'weight');
      fixRoundedTotal(defaultValues.expeditionClientPortions, 100, 'percentage', 4);
      reset(defaultValues);
    },
    [calculateDistributionWeight, previousStepData, reset, sortedClients, totalWeightFromStep1],
  );

  useEffect(() => {
    if (totalPercentage === 100 && isEditingPercentage && totalWeightFromStep1) {
      const weightsTons = weightsToWatch.map(weightToWatch => floatWeightToInt(Number(getValues(weightToWatch))));
      const totalDistributedWeight = weightsTons.reduce((total, weight) => total + weight, 0) / 1000;
      const discrepancy = roundToPrecision(totalWeightFromStep1 - totalDistributedWeight);
      if (discrepancy !== 0) {
        const maxWeightIndex = weightsTons.reduce(
          (maxIdx, currentWeight, idx) => (weightsTons[maxIdx] > currentWeight ? maxIdx : idx),
          0,
        );
        let result = Number(getValues(weightsToWatch[maxWeightIndex])) + discrepancy;
        let formattedResult = result.toFixed(4);
        setValue(weightsToWatch[maxWeightIndex], formattedResult);
      }
    }
  }, [totalPercentage, isEditingPercentage, setValue, totalWeightFromStep1]);

  useEffect(() => {
    if (!provisionalOutboundStocksIndexedByClientId) {
      return;
    }

    if (!reloadStocks) {
      resetInitialValues(provisionalOutboundStocksIndexedByClientId);
    }
  }, [
    sortedClients,
    previousStepData,
    resetInitialValues,
    reset,
    provisionalOutboundStocksIndexedByClientId,
    totalWeightFromStep1,
    reloadStocks,
  ]);
  const shouldCallApiForComputeWeightFromPercentage =
    !isFirstRender.current &&
    !isEditingTonnage &&
    100 === totalPercentage &&
    (!isUpdateMode || (isUpdateMode && isDirty));
  const shouldCallApiForComputePercentageFromWeight =
    !isFirstRender.current && isEditingTonnage && 100 === totalWeight && (!isUpdateMode || (isUpdateMode && isDirty));

  useEffect(() => {
    if (null === provisionalOutboundStocksIndexedByClientId) return;

    const tonnageExpedieKilos = getTonnageExpedieInKilos();
    const contratsClos: ExpeditionFormLine[] = [];
    const contratsEnCours: ExpeditionFormLine[] = [];

    sortedClients.forEach((client, index) => {
      const provisionalOutboundStock = getProvisionalOutboundStock(client) * 1000;

      if (client.sortingCenterContractsStatus === SortingCenterContractStatus.CLOSED && provisionalOutboundStock > 0) {
        contratsClos.push({
          index,
          tonnageEnStockDispoKilos: provisionalOutboundStock,
        });
      } else if (
        client.sortingCenterContractsStatus === SortingCenterContractStatus.ONGOING &&
        provisionalOutboundStock > 0
      ) {
        contratsEnCours.push({
          index,
          tonnageEnStockDispoKilos: provisionalOutboundStock,
        });
      }
    });

    setRepartitionTonnageWithContractStatusPriority(
      transformRepartitionByIndexInFormObjectToArray(
        computeRepartitionByContracts(contratsClos, contratsEnCours, tonnageExpedieKilos ?? 0),
      ),
    );
  }, [provisionalOutboundStocksIndexedByClientId, sortedClients, previousStepData]);

  useEffect(() => {
    let newTotalPercentage = 0;
    let newTotalWeight = 0;

    if (isEditingPercentage && !isSwitchTonnage) {
      for (let i = 0; i < watchPercentages.length; i++) {
        newTotalWeight += calculateWeightFromPercentage(
          parseFloat(watchPercentages[i] ?? '0'),
          totalWeightFromStep1 ?? 0,
        );
        newTotalPercentage += isNaN(parseFloat(watchPercentages[i] ?? '0'))
          ? 0
          : parseFloat(watchPercentages[i] ?? '0');
      }
      setTotalWeight(newTotalWeight);
      setTotalPercentage(newTotalPercentage);
      return;
    }

    if (isEditingTonnage) {
      for (let i = 0; i < watchWeights.length; i++) {
        newTotalPercentage += calculatePercentage(parseFloat(watchWeights[i] ?? '0'), totalWeightFromStep1 ?? 0);
        newTotalWeight += isNaN(parseFloat(watchWeights[i] ?? '0')) ? 0 : parseFloat(watchWeights[i] ?? '0');
      }
      setTotalPercentage(newTotalPercentage);
      setTotalWeight(newTotalWeight);
    } else {
      for (let i = 0; i < watchPercentages.length; i++) {
        newTotalWeight += calculateWeightFromPercentage(
          parseFloat(watchPercentages[i] ?? '0'),
          totalWeightFromStep1 ?? 0,
        );
        newTotalPercentage += isNaN(parseFloat(watchPercentages[i] ?? '0'))
          ? 0
          : parseFloat(watchPercentages[i] ?? '0');
      }
      setTotalWeight(newTotalWeight);
      setTotalPercentage(newTotalPercentage);
    } // see ET-2546 for more informations
  }, [totalWatchPercentage, totalWatchWeight, isEditingTonnage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => {
      if (!totalWeightFromStep1 || !repartitionTonnageWithContractStatusPriority) return;

      if (isFirstRender.current && !expeditionHavePercentageSavedInDatabase) {
        if (isUpdateMode) {
          const weightInKilos: string[] = [];
          const totalWeightFromStep1InKilos = totalWeightFromStep1 * 1000;

          weightsToWatch.forEach((weightToWatch, index) => {
            weightInKilos[index] = (Number(getValues(weightToWatch)) * 1000).toString() ?? '0';
          });

          computeRepartitionPercentageFromWeight(totalWeightFromStep1InKilos, weightInKilos).then(data => {
            percentagesToWatch.forEach((percentageToWatch, index) => {
              setValue(percentageToWatch, data?.repartitionEnPourcentage?.['hydra:member']?.[index]);
            });

            isFirstRender.current = false;
          });
        } else {
          setIsComputeRepartitionLoading(true);

          const tonnageExpedieKilos = getTonnageExpedieInKilos() ?? 0;
          const totalRepartitionKilos = repartitionTonnageWithContractStatusPriority.reduce(
            (previousValue, currentValue) => previousValue + parseInt(currentValue),
            0,
          );

          if (totalRepartitionKilos === tonnageExpedieKilos) {
            computeRepartitionPercentageFromWeight(
              tonnageExpedieKilos,
              repartitionTonnageWithContractStatusPriority,
            ).then(data => {
              percentagesToWatch.forEach((percentageToWatch, index) => {
                const hasRepartition = typeof repartitionTonnageWithContractStatusPriority[index] !== 'undefined';
                const percentage = hasRepartition
                  ? data?.repartitionEnPourcentage?.['hydra:member']?.[index] ?? '0'
                  : '0';
                const checked =
                  hasRepartition && data?.repartitionEnPourcentage?.['hydra:member']?.[index] ? true : false;

                setValue(
                  `expeditionClientPortions.${index}.weight`,
                  hasRepartition
                    ? (parseInt(repartitionTonnageWithContractStatusPriority[index]) / 1000).toString()
                    : '0',
                );
                setValue(percentageToWatch, percentage);
                setValue(`expeditionClientPortions.${index}.checked`, checked);
              });

              isFirstRender.current = false;
              setIsComputeRepartitionLoading(false);
            });
          } else {
            sortedClients.forEach((client, index) => {
              const hasRepartition = typeof repartitionTonnageWithContractStatusPriority[index] !== 'undefined';
              const currentRepartitionKilos = hasRepartition
                ? repartitionTonnageWithContractStatusPriority[index]
                : '0';
              const percentage = hasRepartition
                ? calculatePercentage(parseInt(currentRepartitionKilos), tonnageExpedieKilos ?? 0).toString() ?? '0'
                : '0';

              setValue(
                `expeditionClientPortions.${index}.weight`,
                (parseInt(currentRepartitionKilos) / 1000).toString(),
              );
              setValue(`expeditionClientPortions.${index}.percentage`, percentage);
              setValue(`expeditionClientPortions.${index}.checked`, hasRepartition);
            });
            setIsComputeRepartitionLoading(false);
          }
        }
      } else if (isFirstRender.current && !isUpdateMode) {
        weightsToWatch.forEach((weightToWatch, index) => {
          const portionPercentage = getValues(percentagesToWatch[index]);
          const newWeight = portionPercentage
            ? calculateWeightFromPercentage(parseFloat(portionPercentage), totalWeightFromStep1 ?? 0, 4)
            : '';
          setValue(weightToWatch, String(newWeight));
        });

        isFirstRender.current = false;
      } // see ET-2546 for more informations
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      percentagesToWatch,
      totalWeightFromStep1,
      // Ne pas supprimer "shouldCallApiForComputePercentageFromWeight" sauf si vous savez ce que vous faites.
      // Non utilisé dans le useEffect mais doit rester pour lancer le useEffect quand sa valeur change.
      shouldCallApiForComputePercentageFromWeight,
      repartitionTonnageWithContractStatusPriority,
    ],
  );

  useEffect(() => {
    if (!totalWeightFromStep1) return;

    if (isFromActionCheckedCheckbox.current) {
      isFromActionCheckedCheckbox.current = false;
      return;
    }

    if (shouldCallApiForComputeWeightFromPercentage) {
      const percentages: string[] = [];

      percentagesToWatch.forEach((percentageToWatch, index) => {
        percentages[index] = getValues(percentageToWatch) ?? '0';
      });

      setIsEditingTonnageChanged(true);
      computeRepartitionWeightFromPercentage(totalWeightFromStep1, percentages)
        .then(data => {
          weightsToWatch.forEach((weightToWatch, index) => {
            setValue(weightToWatch, data?.repartitionEnTonnage?.['hydra:member']?.[index]);
          });
        })
        .finally(() => {
          setIsEditingTonnageChanged(false);
        });
    }
  }, [shouldCallApiForComputeWeightFromPercentage]);

  useEffect(() => {
    if (!isFirstRender.current && !isEditingTonnage) {
      let totalPercentage = 0;
      let maxPercentageIndex = 0;
      let maxPercentageValue = 0;

      percentagesToWatch.forEach((percentageToWatch, index) => {
        const weight = watchWeights[index];
        const newPercentage = weight ? calculatePercentage(parseFloat(weight), getTonnageExpedieInTons() ?? 0, 4) : 0;
        totalPercentage += newPercentage;
        if (newPercentage > maxPercentageValue) {
          maxPercentageValue = newPercentage;
          maxPercentageIndex = index;
        }
        setValue(percentageToWatch, String(newPercentage));
        setValue(`expeditionClientPortions.${index}.weight`, String(weight));
      });

      const difference = 100 - totalPercentage; // Remplacez 100 par votre valeur cible si elle est différente
      if (difference !== 0) {
        const adjustedPercentage = maxPercentageValue + difference;
        setValue(percentagesToWatch[maxPercentageIndex], String(adjustedPercentage));
      }
    } else if (isEditingTonnage) {
      isFirstRender.current = false;
      const tonnageExpedieTons = getTonnageExpedieInKilos() ?? 0;
      if (totalWeight === tonnageExpedieTons) {
        const weightsTons: string[] = [];

        weightsToWatch.forEach((weightToWatch, index) => {
          weightsTons[index] = getValues(weightToWatch) ?? '0';
        });
        setIsEditingTonnageChanged(true);

        computeRepartitionPercentageFromWeight(tonnageExpedieTons ?? 0, weightsTons)
          .then(data => {
            percentagesToWatch.forEach((percentageToWatch, index) => {
              setValue(percentageToWatch, data?.repartitionEnPourcentage?.['hydra:member']?.[index] ?? '0');
            });
          })
          .finally(() => {
            setIsEditingTonnageChanged(false);
          });
      } else if (totalPercentage !== 100) {
        weightsToWatch.forEach((weightToWatch, index) => {
          const percentage = watchPercentages[index];
          const newWeigths = percentage
            ? calculateWeightFromPercentage(parseFloat(percentage), getTonnageExpedieInTons() ?? 0, 4)
            : '';
          setValue(weightToWatch, String(newWeigths));
        });
      }
    }
  }, [isEditingTonnage]);

  const handleCheckboxCheck = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (!provisionalOutboundStocksIndexedByClientId) {
      return;
    }

    isFromActionCheckedCheckbox.current = true;
    setHandlingCheckbox(index);

    const tonnageExpedieKilos = getTonnageExpedieInKilos();
    const contratsClos: ExpeditionFormLine[] = [];
    const contratsEnCours: ExpeditionFormLine[] = [];
    const checkboxesValues: boolean[] = [...distributionCheckboxes];
    checkboxesValues[index] = event.target.checked;
    if (!event.target.checked) {
      setValue(`expeditionClientPortions.${index}.weight`, '0.000');
      setValue(`expeditionClientPortions.${index}.percentage`, '0.000');
    }

    sortedClients.forEach((client, index) => {
      const provisionalOutboundStock = getProvisionalOutboundStock(client) * 1000;
      if (
        client.sortingCenterContractsStatus === SortingCenterContractStatus.CLOSED &&
        checkboxesValues[index] &&
        provisionalOutboundStock > 0
      ) {
        contratsClos.push({
          index,
          tonnageEnStockDispoKilos: getProvisionalOutboundStock(client) * 1000,
        });
      } else if (
        client.sortingCenterContractsStatus === SortingCenterContractStatus.ONGOING &&
        checkboxesValues[index] &&
        getProvisionalOutboundStock(client) > 0
      ) {
        contratsEnCours.push({
          index,
          tonnageEnStockDispoKilos: getProvisionalOutboundStock(client) * 1000,
        });
      }
    });

    setValue(`expeditionClientPortions.${index}.checked`, event.target.checked);

    const repartitionByContractsCheckedKilos = transformRepartitionByIndexInFormObjectToArray(
      computeRepartitionByContracts(contratsClos, contratsEnCours, tonnageExpedieKilos ?? 0),
    );

    const totalRepartitionByContractsCheckedKilos = repartitionByContractsCheckedKilos.reduce(
      (previousValue, currentValue) => previousValue + parseInt(currentValue),
      0,
    );

    if (totalRepartitionByContractsCheckedKilos === tonnageExpedieKilos) {
      setIsEditingTonnageChanged(true);
      computeRepartitionPercentageFromWeight(tonnageExpedieKilos ?? 0, repartitionByContractsCheckedKilos)
        .then(data => {
          percentagesToWatch.forEach((percentageToWatch, index) => {
            const hasRepartition = typeof repartitionByContractsCheckedKilos[index] !== 'undefined';
            const percentage = hasRepartition ? data?.repartitionEnPourcentage?.['hydra:member']?.[index] ?? '0' : '0';

            setValue(
              `expeditionClientPortions.${index}.weight`,
              hasRepartition ? (parseInt(repartitionByContractsCheckedKilos[index]) / 1000).toString() : '0',
            );
            setValue(percentageToWatch, percentage);
          });
        })
        .finally(() => {
          setIsEditingTonnageChanged(false);
        });
    } else {
      sortedClients.forEach((client, index) => {
        const hasRepartition = typeof repartitionByContractsCheckedKilos[index] !== 'undefined';
        const currentRepartitionKilos = hasRepartition ? repartitionByContractsCheckedKilos[index] : '0';
        const percentage = hasRepartition
          ? calculatePercentage(parseInt(currentRepartitionKilos), tonnageExpedieKilos ?? 0).toString() ?? '0'
          : '0';

        setValue(`expeditionClientPortions.${index}.weight`, (parseInt(currentRepartitionKilos) / 1000).toString());
        setValue(`expeditionClientPortions.${index}.percentage`, percentage);
      });
    }
    processErrorsAndWarnings();
  };

  const filterNegativeStocks = (isChecked: boolean, index: number) => {
    const availableStock = getValues(`expeditionClientPortions.${index}.availableStockWeight`);
    return availableStock !== null && availableStock > 0;
  };

  const globalCheckboxChecked = () => {
    return distributionCheckboxes.filter(filterNegativeStocks).every(isChecked => isChecked);
  };

  const globalCheckboxIndeterminate = () => {
    return distributionCheckboxes.filter(filterNegativeStocks).some(isChecked => isChecked) && !globalCheckboxChecked();
  };

  const onlyNegativeClosedContracts = useMemo(() => {
    const negativeClosedClients = sortedClients.filter(
      client =>
        client.sortingCenterContractsStatus === SortingCenterContractStatus.CLOSED &&
        getProvisionalOutboundStock(client) < 0,
    );

    const otherStatusClients = sortedClients.filter(
      client =>
        client.sortingCenterContractsStatus !== SortingCenterContractStatus.CLOSED ||
        (client.sortingCenterContractsStatus === SortingCenterContractStatus.CLOSED &&
          getProvisionalOutboundStock(client) >= 0),
    );

    return negativeClosedClients.length > 0 && otherStatusClients.length === 0;
  }, [sortedClients]);

  const updateStatusRows = (statusRow: (number | null)[], index: number, condition: boolean): (number | null)[] => {
    let newRow = [...statusRow];

    if (condition) {
      if (!newRow.includes(index)) {
        newRow.push(index);
      }
    } else {
      newRow = newRow.filter(item => item !== index);
    }

    return newRow;
  };

  const processErrorsAndWarnings = () => {
    const expeditionClientPortions = watch('expeditionClientPortions');

    if (expeditionClientPortions && Array.isArray(expeditionClientPortions)) {
      let newWarningRow: (null | number)[] = [];
      let newErrorRows: (null | number)[] = [];
      expeditionClientPortions.forEach((portion, index) => {
        const { availableStockWeight, weight, checked } = portion;
        const sortingCenterContracts = sortedClients[index];
        const realWeight =
          100 === totalPercentage || (!isUpdateMode && isFirstRender.current)
            ? weight
            : calculateWeightFromPercentage(parseFloat(watchPercentages[index] ?? '0'), totalWeightFromStep1 ?? 0);

        if (!sortingCenterContracts || !checked) {
          return;
        }

        const globalCondition = Boolean(
          availableStockWeight &&
            weight &&
            availableStockWeight < roundToPrecision(Number(realWeight)) &&
            roundToPrecision(Number(realWeight)) > 0,
        );
        const warningCondition =
          globalCondition &&
          Boolean(
            sortingCenterContracts.sortingCenterContractsStatus === SortingCenterContractStatus.END_OF_PROCESSING,
          );
        const errorCondition =
          globalCondition && sortingCenterContracts.sortingCenterContractsStatus === SortingCenterContractStatus.CLOSED;

        newWarningRow = updateStatusRows(newWarningRow, index, warningCondition);
        newErrorRows = updateStatusRows(newErrorRows, index, errorCondition);
      });

      setWarningField(newWarningRow);
      setErrorFields(newErrorRows);
    }
  };

  useEffect(() => {
    trigger('expeditionClientPortions');
  }, [totalWatchWeight, totalWatchPercentage]);

  useEffect(() => {
    if (!isSubmitting) {
      if (warningFields.length > 0) {
        notify(
          {
            message: t('warningRow'),
            severity: 'warning',
          },
          false,
        );
      }

      if (errorFields.length > 0) {
        notify(
          {
            message: t('errorRow'),
            severity: 'error',
          },
          false,
        );
      }

      if (errorFields.length === 0 && warningFields.length === 0) {
        notify(null);
      }
    }
  }, [warningFields, errorFields, isSubmitting]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (errorFields.length === 0) {
      handleSubmit(onSubmit)(e);
    }
  };

  const onChangeCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (!totalWeightFromStep1 || !repartitionTonnageWithContractStatusPriority) return;

      const tonnageExpedieKilos = getTonnageExpedieInKilos() ?? 0;
      const totalRepartitionKilos = repartitionTonnageWithContractStatusPriority.reduce(
        (previousValue, currentValue) => previousValue + parseInt(currentValue),
        0,
      );

      if (totalRepartitionKilos === tonnageExpedieKilos) {
        computeRepartitionPercentageFromWeight(tonnageExpedieKilos, repartitionTonnageWithContractStatusPriority).then(
          data => {
            percentagesToWatch.forEach((percentageToWatch, index) => {
              const hasRepartition = typeof repartitionTonnageWithContractStatusPriority[index] !== 'undefined';
              const percentage = hasRepartition
                ? data?.repartitionEnPourcentage?.['hydra:member']?.[index] ?? '0'
                : '0';
              const checked =
                hasRepartition && data?.repartitionEnPourcentage?.['hydra:member']?.[index] ? true : false;

              setValue(
                `expeditionClientPortions.${index}.weight`,
                hasRepartition
                  ? (parseInt(repartitionTonnageWithContractStatusPriority[index]) / 1000).toString()
                  : '0',
              );
              setValue(percentageToWatch, percentage);
              setValue(`expeditionClientPortions.${index}.checked`, checked);

              trigger('expeditionClientPortions');
            });
          },
        );
      } else {
        sortedClients.forEach((client, index) => {
          const hasRepartition = typeof repartitionTonnageWithContractStatusPriority[index] !== 'undefined';
          const currentRepartitionKilos = hasRepartition ? repartitionTonnageWithContractStatusPriority[index] : '0';
          const percentage = hasRepartition
            ? calculatePercentage(parseInt(currentRepartitionKilos), tonnageExpedieKilos ?? 0).toString() ?? '0'
            : '0';

          setValue(`expeditionClientPortions.${index}.weight`, (parseInt(currentRepartitionKilos) / 1000).toString());
          setValue(`expeditionClientPortions.${index}.percentage`, percentage);
          setValue(`expeditionClientPortions.${index}.checked`, hasRepartition);
        });

        trigger('expeditionClientPortions');
      }
    } else {
      checkboxesToWatch.forEach((checkboxToWatch, index) => {
        setValue(checkboxToWatch, false);
        setValue(weightsToWatch[index], '');
        setValue(percentagesToWatch[index], '');
      });

      trigger('expeditionClientPortions');
    }
  };

  return (
    <>
      <ul className={classes.previousStepInfo}>
        {selectedMaterial && (
          <li>
            <p className={classes.bold}>{t('material')}</p>
            <span>{selectedMaterial.shortName}</span>
          </li>
        )}
        {selectedBuyer && (
          <li>
            <p className={classes.bold}>{t('buyer')}</p>
            <span>{selectedBuyer.name}</span>
          </li>
        )}
        <li>
          <p className={classes.bold}>{t('create.expeditionDate')}</p>
          <span>{previousStepData?.expeditionDate?.toLocaleDateString()}</span>
        </li>
        <li>
          <p className={classes.bold}>{t('weight')}</p>
          <span>{formatTons(totalWeightFromStep1 ?? null)}</span>
        </li>
      </ul>
      {onlyNegativeClosedContracts ? (
        <div className={classes.noContractsContainer}>
          <Typography className={classes.noContracts}>{t('onlyNegativeClosedContracts')}</Typography>
        </div>
      ) : (
        <form id={formName} onSubmit={handleFormSubmit} className={classes.form}>
          <Divider variant="middle" />
          <Box className={classes.tonnageSwitch}>
            <FormControlLabel
              labelPlacement="start"
              label={t('percentageEditing')}
              control={
                <Switch
                  color="primary"
                  checked={isSwitchTonnage}
                  onChange={(_e, value) => {
                    setIsSwitchTonnage(!isSwitchTonnage);
                    track(EVENT_NAMES.boutonPourcentageTonnage, {
                      user_id: user ? user['@id'] : '',
                      code_cdt: sortingCenter?.code,
                      user_role: tGlobal(`users.rolesEnum.${ROLE_HIERARCHY.find(r => roles.includes(r))}`),
                    });
                    if (isSwitchTonnage) {
                      setIsEditingPercentage(false);
                    }
                  }}
                />
              }
            />
          </Box>
          {selectedBuyer && provisionalOutboundStocksIndexedByClientId && !isComputeRepartitionLoading ? (
            <TableContainer className={classes.clientsTable}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.clientNameHeaderCell}>{t('create.client')}</TableCell>
                    <TableCell className={classes.tonnageEnStockHeaderCell}>{t('create.weightStock')}</TableCell>
                    <TableCell className={classes.percentageHeaderCell}>{t('create.distributionInPercent')}</TableCell>
                    <TableCell className={classes.partitioningHeaderCell}>{t('create.distributionInT')}</TableCell>
                    <TableCell className={classes.checkboxHeaderCell}>
                      <Checkbox
                        id={'expeditionClientPortions.checked'}
                        size={'small'}
                        color={'default'}
                        indeterminate={globalCheckboxIndeterminate()}
                        onChange={onChangeCheckAll}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedClients?.map((client, index) => {
                    const provisionalOutboundStock = getProvisionalOutboundStock(client);
                    const shouldDisplayStatusDivider = currentStatus !== client.sortingCenterContractsStatus;
                    if (
                      provisionalOutboundStock <= 0 &&
                      client.sortingCenterContractsStatus === SortingCenterContractStatus.CLOSED
                    ) {
                      return <></>;
                    }

                    currentStatus = client.sortingCenterContractsStatus ?? null;

                    if (!currentStatus) {
                      return <></>;
                    }
                    return (
                      <>
                        {shouldDisplayStatusDivider && (
                          <TableSeparatorRow>
                            {client.sortingCenterContractsStatus
                              ? t(`contractStatus.${client.sortingCenterContractsStatus}`)
                              : t(`contractStatus.${SortingCenterContractStatus.UPCOMING}`)}
                          </TableSeparatorRow>
                        )}
                        <TableRow key={`expeditionClientPortions.${client['@id']}`}>
                          <TableCell
                            className={classNames({
                              [classes.clientNameCell]: true,
                              [classes.negativeWeight]: provisionalOutboundStock < 0,
                            })}
                          >
                            {client.shortName}
                          </TableCell>
                          <TableCell
                            className={classNames({
                              [classes.tonnageEnStockCell]: true,
                              [classes.negativeWeight]: provisionalOutboundStock < 0,
                            })}
                          >
                            <NumberFormat
                              decimalSeparator=","
                              decimalScale={3}
                              isNumericString
                              suffix=" t"
                              fixedDecimalScale={true}
                              displayType="text"
                              value={provisionalOutboundStock}
                            />
                            <input
                              type="hidden"
                              {...register(`expeditionClientPortions.${index}.availableStockWeight`, {
                                value:
                                  provisionalOutboundStocksIndexedByClientId?.[transformObjectId(client)].weight / 1000,
                                shouldUnregister: true,
                              })}
                            />
                          </TableCell>
                          <TableCell
                            className={classNames({
                              [classes.partitioningCell]: true,
                              [classes.warningFields]: warningFields.includes(index),
                              [classes.errorField]: errorFields.includes(index),
                            })}
                          >
                            {isSwitchTonnage ? (
                              <Controller
                                name={`expeditionClientPortions.${index}.percentage`}
                                control={control}
                                shouldUnregister
                                render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => (
                                  <PercentageInputField
                                    id={`expeditionClientPortions.${index}.percentage`}
                                    decimalScale={4}
                                    disabled
                                    variant="standard"
                                    fixedDecimalScale={true}
                                    {...fieldProps}
                                    value={
                                      totalWeightFromStep1 === totalWeight
                                        ? watchPercentages[index]
                                        : calculatePercentage(
                                            parseFloat(watchWeights[index] ?? '0'),
                                            totalWeightFromStep1 ?? 0,
                                          )
                                    }
                                    inputRef={ref}
                                    onBlur={() => {
                                      processErrorsAndWarnings();
                                      trigger('expeditionClientPortions');
                                    }}
                                    onChange={value => {
                                      setValue(`expeditionClientPortions.${index}.percentage`, value);
                                    }}
                                  />
                                )}
                              />
                            ) : (
                              <Controller
                                name={`expeditionClientPortions.${index}.percentage`}
                                control={control}
                                shouldUnregister
                                render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => (
                                  <PercentageInputField
                                    id={`expeditionClientPortions.${index}.percentage`}
                                    disabled={!distributionCheckboxes[index]}
                                    variant="standard"
                                    decimalScale={4}
                                    helperText={fieldState.error?.message}
                                    error={fieldState.invalid}
                                    inputRef={ref}
                                    onBlur={() => {
                                      processErrorsAndWarnings();
                                      trigger('expeditionClientPortions');
                                    }}
                                    {...fieldProps}
                                    onChange={value => {
                                      setValue(`expeditionClientPortions.${index}.percentage`, value);
                                      setIsEditingPercentage(true);
                                    }}
                                  />
                                )}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            className={classNames({
                              [classes.partitioningCell]: true,
                              [classes.warningFields]: warningFields.includes(index),
                              [classes.errorField]: errorFields.includes(index),
                            })}
                          >
                            {isSwitchTonnage ? (
                              <Controller
                                name={`expeditionClientPortions.${index}.weight`}
                                control={control}
                                shouldUnregister
                                render={({ field: { ref, ...fieldProps }, fieldState }) => (
                                  <WeightInputField
                                    id={`expeditionClientPortions.${index}.weight`}
                                    inputRef={ref}
                                    unit="t"
                                    disabled={!distributionCheckboxes[index]}
                                    {...fieldProps}
                                    helperText={fieldState.error?.message}
                                    error={fieldState.invalid}
                                    onBlur={() => {
                                      processErrorsAndWarnings();
                                      trigger([`expeditionClientPortions.${index}.weight`, 'expeditionClientPortions']);
                                    }}
                                    onChange={value => {
                                      if (!isUpdateMode) {
                                        setValue(`expeditionClientPortions.${index}.weight`, value);
                                      } else {
                                        setIsEditingTonnage(true);
                                        setValue(`expeditionClientPortions.${index}.weight`, value);
                                      }
                                    }}
                                  />
                                )}
                              />
                            ) : (
                              <Controller
                                name={`expeditionClientPortions.${index}.weight`}
                                control={control}
                                shouldUnregister
                                render={({ field: { ref, ...fieldProps }, fieldState }) => (
                                  <WeightInputField
                                    id={`expeditionClientPortions.${index}.weight`}
                                    decimalScale={3}
                                    isNumericString
                                    unit="t"
                                    disabled
                                    fixedDecimalScale={true}
                                    {...fieldProps}
                                    onBlur={() => {
                                      processErrorsAndWarnings();
                                      trigger([`expeditionClientPortions.${index}.weight`, 'expeditionClientPortions']);
                                    }}
                                    onChange={value => {
                                      setValue(`expeditionClientPortions.${index}.weight`, value);
                                      setIsEditingTonnage(value);
                                    }}
                                    value={
                                      100 === totalPercentage || (isUpdateMode && isFirstRender.current)
                                        ? watchWeights[index]
                                        : calculateWeightFromPercentage(
                                            parseFloat(watchPercentages[index] ?? '0'),
                                            totalWeightFromStep1 ?? 0,
                                          )
                                    }
                                  />
                                )}
                              />
                            )}
                            <input
                              hidden
                              {...register(`expeditionClientPortions.${index}.client`, { shouldUnregister: true })}
                              value={client['@id']}
                            />
                          </TableCell>
                          <TableCell className={classes.checkboxCell}>
                            <Controller
                              name={`expeditionClientPortions.${index}.checked`}
                              control={control}
                              render={({ field: { ref, value, onChange, ...fieldProps } }) => (
                                <Checkbox
                                  id={`expeditionClientPortions.${index}.checked`}
                                  inputRef={ref}
                                  size={'small'}
                                  color={'default'}
                                  checked={value ?? true}
                                  value={value ?? true}
                                  {...fieldProps}
                                  onChange={event => handleCheckboxCheck(event, index)}
                                />
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })}
                  <TableSeparatorRow>Total</TableSeparatorRow>
                  <TableRow>
                    <TableCell className={classes.clientNameCell} />
                    <TableCell className={classes.totalCell} />
                    <TableCell className={classes.totalCell}>
                      <div>
                        {isSwitchTonnage ? (
                          <WeightInputField
                            decimalScale={4}
                            isNumericString
                            unit="%"
                            disabled
                            fixedDecimalScale={true}
                            displayType="text"
                            value={totalPercentage}
                          />
                        ) : (
                          <NumberFormat
                            decimalSeparator=","
                            decimalScale={4}
                            isNumericString
                            suffix="%"
                            fixedDecimalScale={true}
                            displayType="text"
                            value={totalPercentage}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={classes.totalCell}>
                      <div>
                        {isSwitchTonnage ? (
                          <NumberFormat
                            decimalSeparator=","
                            decimalScale={3}
                            isNumericString
                            suffix=" t"
                            fixedDecimalScale={true}
                            displayType="text"
                            value={totalWeight}
                          />
                        ) : (
                          <WeightInputField
                            decimalScale={3}
                            isNumericString
                            unit="t"
                            disabled
                            fixedDecimalScale={true}
                            value={totalWeight}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={classes.totalCell}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {/* @ts-ignore */}
              {errors?.expeditionClientPortions?.message && (
                // @ts-ignore
                <FormHelperText error>{errors?.expeditionClientPortions?.message}</FormHelperText>
              )}
            </TableContainer>
          ) : (
            <Box p={2}>
              <Loading />
            </Box>
          )}
        </form>
      )}
    </>
  );
};

export default Step2;
